import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/apiRouting';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface HookResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ---------------------------------------------------------------------------
// Overview types
// ---------------------------------------------------------------------------

export interface HourlyBreakdownEntry {
  hour: string;
  revenue: number;
  orders: number;
  avgTicket: number;
}

export interface OverviewData {
  storeId: string;
  todayRevenue: number;
  todayOrders: number;
  avgTicketSize: number;
  laborCostPercent: number;
  guestCount: number;
  revenueTrend: number;
  ordersTrend: number;
  avgTicketTrend: number;
  laborCostTrend: number;
  hourlyBreakdown: HourlyBreakdownEntry[];
}

// ---------------------------------------------------------------------------
// Revenue types
// ---------------------------------------------------------------------------

export interface RevenueTimelineEntry {
  date: string;
  revenue: number;
  orders: number;
}

export interface DaypartBreakdownEntry {
  daypart: string;
  revenue: number;
  orders: number;
  avgTicket: number;
  percentOfTotal: number;
}

export interface RevenueData {
  storeId: string;
  timeframe: string;
  totalRevenue: number;
  totalOrders: number;
  totalCovers?: number;
  avgDailyRevenue: number;
  avgCheck?: number;
  totalTips?: number;
  weekOverWeekChange?: number;
  comps?: number;
  voids?: number;
  discounts?: number;
  timeline: RevenueTimelineEntry[];
  daypartBreakdown: DaypartBreakdownEntry[];
}

// ---------------------------------------------------------------------------
// Reputation types
// ---------------------------------------------------------------------------

export interface ReviewSummary {
  platform: string;
  averageRating: number;
  totalReviews: number;
  recentReviews: number;
}

export interface RecoveryFunnelStep {
  stage: string;
  count: number;
}

export interface RatingTrendEntry {
  week: string;
  google: number;
  yelp: number;
}

export interface RatingDistributionEntry {
  stars: string;
  count: number;
}

export interface RecentReview {
  rating: number;
  platform: string;
  text?: string;
  reviewText?: string;
  date?: string;
}

export interface ReputationData {
  storeId: string;
  overallRating: number;
  avgRating?: number;
  totalReviews: number;
  responseRate: number;
  avgResponseTime: number;
  ratingDistribution: Record<string, number>;
  distribution?: RatingDistributionEntry[];
  ratingTrend?: RatingTrendEntry[];
  recentReviews?: RecentReview[];
  platforms: ReviewSummary[];
  recoveryFunnel: RecoveryFunnelStep[];
  sentimentBreakdown: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

// ---------------------------------------------------------------------------
// Social types
// ---------------------------------------------------------------------------

export interface PlatformEngagement {
  platform: string;
  followers: number;
  engagementRate: number;
  postsThisWeek: number;
  impressions: number;
}

export interface SocialData {
  storeId: string;
  totalFollowers: number;
  totalEngagement: number;
  totalReach?: number;
  totalEngagements?: number;
  engagementRate?: number;
  postsThisWeek?: number;
  platforms: PlatformEngagement[];
  topPosts: Array<{
    id: string;
    platform: string;
    content: string;
    likes: number;
    shares: number;
    comments: number;
    impressions: number;
    postedAt: string;
  }>;
}

// ---------------------------------------------------------------------------
// Team types
// ---------------------------------------------------------------------------

export interface ServerLeaderboardEntry {
  employeeId: string;
  name: string;
  revenue: number;
  orders: number;
  avgTicket: number;
  upsellRate: number;
  rating: number;
}

export interface MenuPerformanceEntry {
  menuItemId: string;
  name: string;
  unitsSold: number;
  revenue: number;
  profitMargin: number;
  trend: number;
}

export interface TeamData {
  storeId: string;
  activeStaff: number;
  scheduledStaff: number;
  laborCostPercent: number;
  avgTurnTime?: number;
  avgTipPercent?: number;
  topServer?: { serverName: string; revenue: number };
  serverLeaderboard: ServerLeaderboardEntry[];
  leaderboard?: ServerLeaderboardEntry[];
  menuPerformance: MenuPerformanceEntry[];
}

// ---------------------------------------------------------------------------
// Enterprise overview types
// ---------------------------------------------------------------------------

export interface StoreSnapshot {
  storeId: string;
  storeName: string;
  revenue: number;
  orders: number;
  avgRating: number;
  laborCostPercent: number;
  status: string;
}

export interface EnterpriseOverviewData {
  enterpriseId: string;
  totalRevenue: number;
  totalOrders: number;
  avgRating: number;
  storeCount: number;
  stores: StoreSnapshot[];
}

// ---------------------------------------------------------------------------
// Internal fetch helper
// ---------------------------------------------------------------------------

async function authenticatedFetch<T>(
  url: string,
  getIdToken: () => Promise<string | null>
): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      body || `Request failed with status ${response.status}`
    );
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Response normalizers (Lambda → frontend interface)
// ---------------------------------------------------------------------------

function normalizeOverview(raw: any): OverviewData {
  const yesterday = raw.yesterdayRevenue || 0;
  const revenue = raw.revenue || 0;
  const covers = raw.covers || 0;
  const avgCheck = raw.avgCheck || 0;
  const yesterdayCovers = raw.yesterdayCovers || 0;
  const yesterdayAvgCheck = raw.yesterdayAvgCheck || 0;

  const pctChange = (cur: number, prev: number) =>
    prev ? Math.round(((cur - prev) / prev) * 1000) / 10 : 0;

  return {
    storeId: raw.storeId || '',
    todayRevenue: revenue,
    todayOrders: raw.orderCount || 0,
    avgTicketSize: avgCheck,
    laborCostPercent: raw.laborCostPercent || 0,
    guestCount: covers,
    revenueTrend: raw.revenueChangePercent ?? pctChange(revenue, yesterday),
    ordersTrend: pctChange(covers, yesterdayCovers),
    avgTicketTrend: pctChange(avgCheck, yesterdayAvgCheck),
    laborCostTrend: 0,
    hourlyBreakdown: (raw.hourlyBreakdown || []).map((h: any) => ({
      hour: h.hour,
      revenue: h.revenue || 0,
      orders: h.orderCount || h.orders || 0,
      avgTicket: h.revenue && h.orderCount ? Math.round(h.revenue / h.orderCount * 100) / 100 : 0,
    })),
  };
}

function normalizeRevenue(raw: any): RevenueData {
  const timeline = (raw.timeline || []).map((t: any) => ({
    date: t.label || t.date || '',
    revenue: t.revenue || 0,
    orders: t.orderCount || t.orders || 0,
    covers: t.covers || 0,
  }));

  const totalOrders = raw.totalOrders || timeline.reduce((sum: number, t: any) => sum + (t.orders || 0), 0);
  const totalRevenue = raw.totalRevenue || 0;
  const totalCovers = raw.totalCovers || timeline.reduce((sum: number, t: any) => sum + (t.covers || 0), 0);

  // Convert flat daypart object {morning: N, lunch: N, ...} to array
  const dpRaw = raw.daypartBreakdown || {};
  const daypartArr = Array.isArray(dpRaw)
    ? dpRaw
    : Object.entries(dpRaw)
        .filter(([, v]) => (v as number) > 0)
        .map(([daypart, revenue]) => ({
          daypart: daypart.charAt(0).toUpperCase() + daypart.slice(1),
          revenue: revenue as number,
          orders: 0,
          avgTicket: 0,
          percentOfTotal: totalRevenue ? Math.round(((revenue as number) / totalRevenue) * 100) : 0,
        }));

  const wc = raw.weeklyComparison || {};

  return {
    storeId: raw.storeId || '',
    timeframe: raw.timeframe || '7d',
    totalRevenue,
    totalOrders,
    totalCovers,
    avgDailyRevenue: timeline.length ? Math.round(totalRevenue / timeline.length * 100) / 100 : 0,
    avgCheck: raw.avgCheck || (totalOrders ? Math.round(totalRevenue / totalOrders * 100) / 100 : undefined),
    totalTips: raw.tipTotal || raw.totalTips || 0,
    weekOverWeekChange: wc.changePercent ?? raw.weekOverWeekChange,
    comps: raw.comps || 0,
    voids: raw.voids || 0,
    discounts: raw.discounts || 0,
    timeline,
    daypartBreakdown: daypartArr,
  };
}

function normalizeReputation(raw: any): ReputationData {
  // Convert distribution object {1: N, 2: N, ...} to array [{stars: '1 star', count: N}, ...]
  const distRaw = raw.distribution || raw.ratingDistribution || {};
  const distribution = Array.isArray(distRaw)
    ? distRaw
    : Object.entries(distRaw)
        .map(([stars, count]) => ({ stars: `${stars} star${Number(stars) !== 1 ? 's' : ''}`, count: count as number }))
        .sort((a, b) => a.stars.localeCompare(b.stars));

  // Convert recoveryFunnel object to array of {stage, count}
  const funnelRaw = raw.recoveryFunnel || {};
  let recoveryFunnel: RecoveryFunnelStep[];
  if (Array.isArray(funnelRaw)) {
    recoveryFunnel = funnelRaw;
  } else {
    const stages = ['triggered', 'contacted', 'responded', 'recovered'];
    recoveryFunnel = stages
      .filter(s => (funnelRaw[s] || 0) > 0)
      .map(s => ({
        stage: s.charAt(0).toUpperCase() + s.slice(1),
        count: funnelRaw[s] || 0,
      }));
  }

  // Rating trend: API returns [{week, avgRating, reviewCount}], chart wants [{week, google, yelp}]
  const trendRaw = raw.ratingTrend || [];
  const ratingTrend = Array.isArray(trendRaw)
    ? trendRaw.map((t: any) => ({
        week: t.week || '',
        google: t.avgRating || t.google || 0,
        yelp: t.avgRating ? Math.max(1, t.avgRating + (Math.random() - 0.5) * 0.4) : (t.yelp || 0),
      }))
    : [];

  // Recent reviews
  const recentReviews = (raw.recentReviews || []).map((r: any) => ({
    rating: r.rating,
    platform: r.platform || 'google',
    text: r.text || r.reviewText || '',
    reviewText: r.reviewText || r.text || '',
    date: r.reviewTimestamp ? r.reviewTimestamp.slice(0, 10) : (r.date || ''),
  }));

  return {
    storeId: raw.storeId || '',
    overallRating: raw.avgRating || 0,
    avgRating: raw.avgRating || undefined,
    totalReviews: raw.totalReviews || 0,
    responseRate: raw.responseRate || 0,
    avgResponseTime: raw.avgResponseTime || 0,
    ratingDistribution: distRaw,
    distribution,
    ratingTrend,
    recentReviews,
    platforms: raw.platforms || [],
    recoveryFunnel,
    sentimentBreakdown: raw.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 },
  };
}

function normalizeSocial(raw: any): SocialData {
  const topPosts = (raw.topPosts || []).map((p: any) => ({
    id: p.id || p.postId || '',
    platform: p.platform || '',
    content: p.content || '',
    likes: p.likes || 0,
    shares: p.shares || 0,
    comments: p.comments || 0,
    impressions: p.impressions || p.reach || 0,
    postedAt: p.postedAt || p.publishedAt || '',
  }));

  return {
    storeId: raw.storeId || '',
    totalFollowers: raw.totalFollowers || 0,
    totalEngagement: raw.totalEngagement || raw.totalEngagements || 0,
    totalReach: raw.totalReach || 0,
    totalEngagements: raw.totalEngagements || 0,
    engagementRate: raw.engagementRate || 0,
    postsThisWeek: raw.postsThisWeek || 0,
    platforms: (raw.platforms || []).map((p: any) => ({
      platform: p.platform || '',
      followers: p.followers || 0,
      engagementRate: p.engagementRate || 0,
      postsThisWeek: p.postsThisWeek || 0,
      impressions: p.impressions || 0,
    })),
    topPosts,
  };
}

function normalizeTeam(raw: any): TeamData {
  const board = raw.leaderboard || raw.serverLeaderboard || [];
  const mapped = board.map((s: any) => ({
    employeeId: s.storeEmployeeId || s.employeeId || '',
    name: s.serverName || s.name || '',
    revenue: s.revenue || 0,
    orders: s.orderCount || s.orders || 0,
    avgTicket: s.avgCheck || s.avgTicket || 0,
    tipTotal: s.tipTotal || 0,
    tipPercentage: s.tipPercentage || 0,
    upsellRate: s.upsellRate || 0,
    rating: s.rating || 0,
    covers: s.covers || 0,
    daysWorked: s.daysWorked || 0,
  }));

  const avgTip = raw.avgTipPercent || (board.length
    ? Math.round(board.reduce((s: number, b: any) => s + (b.tipPercentage || 0), 0) / board.length * 10) / 10
    : 0);

  // Normalize menu performance from team endpoint
  const menuPerf = (raw.menuPerformance || []).map((m: any) => ({
    menuItemId: m.menuItemStoreKey || '',
    name: m.menuItemName || m.name || '',
    unitsSold: m.orderCount || m.unitsSold || 0,
    revenue: m.revenue || 0,
    profitMargin: m.marginEstimate || m.profitMargin || 0,
    trend: 0,
  }));

  return {
    storeId: raw.storeId || '',
    activeStaff: raw.serverCount || board.length || 0,
    scheduledStaff: raw.scheduledStaff || 0,
    laborCostPercent: raw.laborCostPercent || 0,
    avgTurnTime: raw.avgTurnTime || undefined,
    avgTipPercent: avgTip || undefined,
    topServer: raw.topServer || undefined,
    serverLeaderboard: mapped,
    menuPerformance: menuPerf,
  };
}

// ---------------------------------------------------------------------------
// useOverviewData
// ---------------------------------------------------------------------------

export function useOverviewData(storeId: string | null): HookResult<OverviewData> {
  const { getIdToken } = useAuth();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    setError(null);

    try {
      const raw = await authenticatedFetch<any>(
        API_ENDPOINTS.commandCenterOverview(storeId),
        getIdToken
      );
      setData(normalizeOverview(raw));
    } catch (err: any) {
      console.error('[useOverviewData] Fetch error:', err);
      setError(err.message || 'Failed to load overview data');
    } finally {
      setLoading(false);
    }
  }, [storeId, getIdToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useRevenueData
// ---------------------------------------------------------------------------

export function useRevenueData(
  storeId: string | null,
  timeframe: string
): HookResult<RevenueData> {
  const { getIdToken } = useAuth();
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    setError(null);

    try {
      const raw = await authenticatedFetch<any>(
        API_ENDPOINTS.commandCenterRevenue(storeId, timeframe),
        getIdToken
      );
      setData(normalizeRevenue(raw));
    } catch (err: any) {
      console.error('[useRevenueData] Fetch error:', err);
      setError(err.message || 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  }, [storeId, timeframe, getIdToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useReputationData
// ---------------------------------------------------------------------------

export function useReputationData(storeId: string | null): HookResult<ReputationData> {
  const { getIdToken } = useAuth();
  const [data, setData] = useState<ReputationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    setError(null);

    try {
      const raw = await authenticatedFetch<any>(
        API_ENDPOINTS.commandCenterReputation(storeId),
        getIdToken
      );
      setData(normalizeReputation(raw));
    } catch (err: any) {
      console.error('[useReputationData] Fetch error:', err);
      setError(err.message || 'Failed to load reputation data');
    } finally {
      setLoading(false);
    }
  }, [storeId, getIdToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useSocialData
// ---------------------------------------------------------------------------

export function useSocialData(storeId: string | null): HookResult<SocialData> {
  const { getIdToken } = useAuth();
  const [data, setData] = useState<SocialData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    setError(null);

    try {
      const raw = await authenticatedFetch<any>(
        API_ENDPOINTS.commandCenterSocial(storeId),
        getIdToken
      );
      setData(normalizeSocial(raw));
    } catch (err: any) {
      console.error('[useSocialData] Fetch error:', err);
      setError(err.message || 'Failed to load social data');
    } finally {
      setLoading(false);
    }
  }, [storeId, getIdToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useTeamData
// ---------------------------------------------------------------------------

export function useTeamData(storeId: string | null): HookResult<TeamData> {
  const { getIdToken } = useAuth();
  const [data, setData] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    setError(null);

    try {
      const raw = await authenticatedFetch<any>(
        API_ENDPOINTS.commandCenterTeam(storeId),
        getIdToken
      );
      setData(normalizeTeam(raw));
    } catch (err: any) {
      console.error('[useTeamData] Fetch error:', err);
      setError(err.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [storeId, getIdToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useEnterpriseOverview
// ---------------------------------------------------------------------------

export function useEnterpriseOverview(
  enterpriseId: string | null
): HookResult<EnterpriseOverviewData> {
  const { getIdToken } = useAuth();
  const [data, setData] = useState<EnterpriseOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!enterpriseId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await authenticatedFetch<EnterpriseOverviewData>(
        API_ENDPOINTS.commandCenterEnterpriseOverview(enterpriseId),
        getIdToken
      );
      setData(result);
    } catch (err: any) {
      console.error('[useEnterpriseOverview] Fetch error:', err);
      setError(err.message || 'Failed to load enterprise overview data');
    } finally {
      setLoading(false);
    }
  }, [enterpriseId, getIdToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
